import { mapValues } from 'lodash';
import { useEffect, useRef } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import { AccountIdPropertyFormData } from 'dashboard/forms/FormAccountIdProperty';
import { FormUpdateTagsData } from 'dashboard/forms/FormUpdateTags';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { Account, FieldsMapLegacyUser } from 'dashboard/models/account';
import { AccountAction } from 'dashboard/models/account-action';
import { ProductFeatureName } from 'dashboard/models/organization';
import { ReportTemplateFilterOption } from 'dashboard/models/report-template';
import { WorkflowStatus } from 'dashboard/models/workflow';
import {
  WORKFLOW_RUN_TERMINAL_STATES,
  WorkflowRun,
  WorkflowRunStatus,
} from 'dashboard/models/workflow-run';
import { showMessage } from 'dashboard/redux/actions/messages';
import { getAccountGenericDocuments } from 'dashboard/redux/selectors/account';
import { getAllTemplateFilterOptions } from 'dashboard/redux/selectors/organization';
import {
  clearAccount,
  setAccount,
  setAccountRedactor,
  setDocumentCurrentPage,
  setDocumentPageSize,
  setInquiryCurrentPage,
  setInquiryPageSize,
  setReportCurrentPage,
  setReportPageSize,
  setVerificationCurrentPage,
  setVerificationPageSize,
} from 'dashboard/redux/slices/account';
import { RootState } from 'dashboard/redux/store';
import {
  loadAccount,
  loadAccountDocuments,
  loadAccountInquiries,
  loadAccountReports,
  loadAccountVerifications,
} from 'dashboard/redux/thunk-actions/account';
import { loadAccountActions } from 'dashboard/redux/thunk-actions/account-actions';
import { loadAccountStatuses } from 'dashboard/redux/thunk-actions/account-statuses';
import {
  loadAccountTypes,
  loadAccountTypesFilterOptions,
} from 'dashboard/redux/thunk-actions/account-types';
import { createCase } from 'dashboard/redux/thunk-actions/case';
import { loadCaseTemplateBasicsOptions } from 'dashboard/redux/thunk-actions/case-templates';
import { loadGraphsAndGraph } from 'dashboard/redux/thunk-actions/graph';
import { loadGraphQueryTemplates } from 'dashboard/redux/thunk-actions/graph-query-templates';
import { createReport } from 'dashboard/redux/thunk-actions/report';
import { loadReportTemplatesFilterOptions } from 'dashboard/redux/thunk-actions/report-templates';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import DashboardAccountsShowView from 'dashboard/views/DashboardAccountsShowView';
import { CreateCaseFormData } from 'dashboard/views/DashboardAccountsShowView/components/AccountCreateCaseModal';
import ApiResponseError from 'lib/errors/ApiResponseError';
import { FieldArray, FieldsMap } from 'lib/fields';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import usePermission from 'lib/hooks/usePermission';
import useProductFeature from 'lib/hooks/useProductFeature';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import {
  conflictErrorString,
  isConflictError,
  isPermissionsError,
  permissionsErrorMessage,
} from 'lib/permissions-errors';
import { captureException } from 'lib/reporting';
import { Json } from 'lib/types';

const nullStateMessages: Record<number, string> = {
  401: "You don't have permissions to view this Account.",
  404: 'We couldn’t find that Account.',
};

type DashboardAccountsShowRouteProps = PropsFromRedux;

type Params = { accountOrReferenceId: string };

const DashboardAccountsShowRoute = (props: DashboardAccountsShowRouteProps) => {
  const navigate = useNavigate();
  const params = useParams<Params>() as Params;
  const isGraphEnabled = useProductFeature(ProductFeatureName.Graph);
  const hasGraphViewPermission = usePermission(Permission.GraphView);
  const hasGraphQueryTemplateViewPermission = usePermission(Permission.GraphQueryTemplateView);
  const hasCaseCreatePermission = usePermission(Permission.CaseCreate);

  useOnMountAndUnmount(props.clearAccount);

  useRedirectOnEnvironmentChange('/dashboard/accounts');
  const errorCode = useRef<number>(null);

  const accountTypeId = props.account?.relationships['account-type']?.data.id;

  const {
    loading: isLoadingAccountReportTemplates,
    callback: loadAccountReportTemplateFilterOptions,
  } = useApiCallback(async (accountId: string) => {
    await props.loadReportTemplatesFilterOptions({
      filters: { accountId: accountId },
    });
  }, []);

  useApiLoader({
    loader: () => props.loadUserList(),
    deps: [props.loadUserList],
    onError: () => props.showMessage('Failed to load users.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadCaseTemplateBasicsOptions(),
    deps: [props.loadCaseTemplateBasicsOptions],
    skip: !hasCaseCreatePermission,
    onError: () => props.showMessage('Failed to load case templates.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // -----------------------------------------------------------------------------------------
  // Account Action
  // -----------------------------------------------------------------------------------------
  let pollWorkflowRun: number;
  const POLL_INTERVAL = 1000;
  const MAX_TIMEOUT = 10;
  const pollForWorkflowRunComplete = async (maxTimeOut: number, workflowRunId: string) => {
    if (maxTimeOut < 0) {
      // Stop polling if workflow is not transitioned into terminal state in 10 sec
      clearTimeout(pollWorkflowRun);
      return;
    }

    const pendingWorkflowRun = (
      await SessionClientDashboard.getResource<WorkflowRun>(ApiEndpoint.WorkflowRun, workflowRunId)
    ).data;

    if (WORKFLOW_RUN_TERMINAL_STATES.includes(pendingWorkflowRun.attributes.status)) {
      clearTimeout(pollWorkflowRun);
      await Promise.all([
        loadAccount(),
        loadAccountInquiries(),
        loadAccountVerifications(),
        loadAccountReports(),
        loadAccountDocuments(),
      ]);

      if (pendingWorkflowRun.attributes.status === WorkflowRunStatus.Completed) {
        props.showMessage('Action successfully completed.', MessageType.Success);
      } else {
        props.showMessage('Action failed to run.', MessageType.Failure);
      }
    } else {
      pollWorkflowRun = window.setTimeout(() => {
        pollForWorkflowRunComplete(maxTimeOut - 1, workflowRunId);
      }, POLL_INTERVAL);
    }
  };

  const { loading: isLoadingAccountActions } = useApiLoader({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    loader: () => {
      if (accountTypeId) {
        return props.loadAccountActions(
          accountTypeId,
          { status: [WorkflowStatus.Running] },
          {
            sort: 'created_at',
            shouldSortDescending: true,
          },
        );
      }
    },
    deps: [accountTypeId, props.loadAccountActions],
    onError: () => {
      props.showMessage(
        'Failed to load account actions, please reload to try again',
        MessageType.Failure,
      );
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const { callback: handleRunAccountAction, error: handleRunAccountActionError } = useApiCallback(
    async (values: Json, accountActionId: string) => {
      const response = await SessionClientDashboard.postResourceAction<AccountAction>(
        {
          type: ApiModel.AccountAction,
          id: accountActionId,
        },
        'run',
        {
          meta: {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            'account-id': props.account.id,
            'trigger-params': values,
          },
        },
      );

      props.showMessage('Started running action in the background.', MessageType.Success);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await pollForWorkflowRunComplete(MAX_TIMEOUT, response.meta['workflow-run-id']);
    },
    [props.account],
  );

  useApiErrorHandler({
    error: handleRunAccountActionError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        // don't show anything if this is a permission error since the action is likely running,
        // we just can't poll the endpoint to confirm.
      } else {
        captureException(error);
        props.showMessage('Failed to run action', MessageType.Failure);
      }
    },
    onNetworkError: () => props.showMessage('Failed to run action', MessageType.Failure),
  });

  // Initially load account from account or reference-id param. Then load all account's dependencies
  // after.
  const loadDocumentsSeparately = useFeatureFlag('dashboard:accounts-paginate-documents');
  const loadReportsSeparately = useFeatureFlag('dashboard:accounts-paginate-account-objects');
  const { loading: isLoadingAccount, callback: loadAccount } = useApiLoader({
    loader: () => props.loadAccount(params.accountOrReferenceId, loadDocumentsSeparately),
    deps: [props.loadAccount, params.accountOrReferenceId],
    onError: (err: ApiResponseError) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to load account.', MessageType.Failure);
      }

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = err.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingGraph } = useApiLoader({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    loader: () => {
      if (isGraphEnabled && hasGraphViewPermission) {
        return props.loadGraphsAndGraph(props.currentEnvironment.id);
      } else {
        return;
      }
    },
    deps: [props.currentEnvironment],
    onError: () => {
      props.showMessage('Failed to load graph, please reload to try again', MessageType.Failure);
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const { loading: isLoadingGraphQueryTemplates } = useApiLoader({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    loader: () => {
      if (isGraphEnabled && hasGraphQueryTemplateViewPermission) {
        return props.loadGraphQueryTemplates();
      } else {
        return;
      }
    },
    deps: [],
    onError: () => {
      props.showMessage(
        'Failed to load graph query templates, please reload to try again',
        MessageType.Failure,
      );
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  useApiLoader({
    loader: () => {
      return props.loadAccountTypes();
    },
    deps: [props.currentEnvironment],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to load account types, please reload to try again',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      );
    },
  });

  useApiLoader({
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    loader: () => {
      if (accountTypeId) {
        return props.loadAccountStatuses(accountTypeId);
      }
    },
    deps: [accountTypeId, props.account, props.loadAccountStatuses],
    onError: (err: ApiResponseError) => {
      props.showMessage('Failed to load account statuses.', MessageType.Failure);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = err.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  // -----------------------------------------------------------------------------------------
  // Account status loading
  // -----------------------------------------------------------------------------------------
  const { loading: isLoadingAccountTypes } = useApiLoader({
    loader: () => props.loadAccountTypesFilterOptions(),
    deps: [props.loadAccountTypesFilterOptions],
    onError: () => {
      props.showMessage('Failed to load graph, please reload to try again', MessageType.Failure);
    },
    onNetworkError: () => {
      props.showMessage(
        'We are experiencing network issues. Please reload to try again.',
        MessageType.Failure,
      );
    },
  });

  const { loading: isLoadingInquiries, callback: loadAccountInquiries } = useApiLoader({
    loader: () =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      props.account != null
        ? props.loadAccountInquiries({
            accountId: props.account.id,
            currentEnvironment: props.currentEnvironment,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            currentPage: props.inquiryCurrentPage,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            pageSize: props.inquiryPageSize,
          })
        : null,
    deps: [
      props.account,
      props.currentEnvironment,
      props.inquiryCurrentPage,
      props.inquiryPageSize,
    ],
    onError: (err: ApiResponseError) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to load inquiries.', MessageType.Failure);
      }
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = err.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingVerifications, callback: loadAccountVerifications } = useApiLoader({
    loader: () =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      props.account != null
        ? props.loadAccountVerifications({
            accountId: props.account.id,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            currentPage: props.verificationCurrentPage,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            pageSize: props.verificationPageSize,
          })
        : null,
    deps: [
      props.account,
      props.currentEnvironment,
      props.verificationCurrentPage,
      props.verificationPageSize,
    ],
    onError: (err: ApiResponseError) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to load verifications.', MessageType.Failure);
      }
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = err.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingReports, callback: loadAccountReports } = useApiLoader({
    loader: () =>
      props.account != null
        ? props.loadAccountReports({
            accountId: props.account.id,
            currentPage: props.reportCurrentPage ?? 1,
            pageSize: props.reportPageSize ?? 10,
          })
        : Promise.resolve(),
    skip: !loadReportsSeparately,
    deps: [props.account, props.currentEnvironment, props.reportCurrentPage, props.reportPageSize],
    onError: (err: ApiResponseError) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to load reports.', MessageType.Failure);
      }
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = err.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: isLoadingDocuments, callback: loadAccountDocuments } = useApiLoader({
    loader: () =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      props.account != null
        ? props.loadAccountDocuments({
            accountId: props.account.id,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            currentPage: props.documentCurrentPage,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            pageSize: props.documentPageSize,
          })
        : null,
    deps: [
      props.account,
      props.currentEnvironment,
      props.documentCurrentPage,
      props.documentPageSize,
    ],
    skip: !loadDocumentsSeparately,
    onError: (err: ApiResponseError) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else {
        props.showMessage('Failed to load documents.', MessageType.Failure);
      }
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      errorCode.current = err.status;
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: updateAccountError, callback: handleUpdateAccount } = useApiCallback(
    // unfortunately, using FormUpdateAccountNewData as the values here causes a recursive
    // type instantiation...FormUpdateAccountNewData is not particularly detailed anyway, so
    // using `any` here instead.
    async (values: any) => {
      const attributes = {
        ...mapValues(values, (value) => value ?? null),
      };
      await SessionClientDashboard.updateResource<Account>({
        /*
         * Due to mapValues' inability to recognize the types of values
         * present in the object, I had to find a way to avoid the type
         * check in this particular scenario.
         * */
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        attributes: attributes,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        id: props.account.id,
        type: ApiModel.Account,
      });

      await loadAccount();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await loadAccountReportTemplateFilterOptions(props.account.id);
      props.showMessage(`Account updated.`, MessageType.Success);
    },
    [
      props.showMessage,
      props.setAccount,
      props.account,
      loadAccount,
      loadAccountReportTemplateFilterOptions,
    ],
  );

  useApiErrorHandler({
    error: updateAccountError,
    onError: (error) => {
      if (isConflictError(error)) {
        props.showMessage(conflictErrorString(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to update account. Please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: createIdNumberError, callback: handleCreateIdNumber } = useApiCallback(
    async (values: {
      'identification-number': string;
      'identification-class': string;
      'country-code': string;
    }) => {
      const attributes = {
        fields: {
          'identification-numbers': {
            type: 'array',
            value: [
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              ...(props.account.attributes.fields['identification_numbers'] as FieldArray)['value'],
              {
                type: 'hash',
                value: {
                  'identification-number': {
                    type: 'string',
                    value: values['identification-number'],
                  },
                  'identification-class': { type: 'string', value: values['identification-class'] },
                  'issuing-country': { type: 'string', value: values['country-code'] },
                },
              },
            ],
          },
        } as FieldsMap,
      };

      await SessionClientDashboard.updateResource({
        attributes,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        id: props.account.id,
        type: ApiModel.Account,
      });

      await loadAccount();
      props.showMessage(`Account updated.`, MessageType.Success);
    },
    [props.account, props.showMessage, loadAccount],
  );

  useApiErrorHandler({
    error: createIdNumberError,
    onError: () =>
      props.showMessage('Failed to add id number. Please try again.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const {
    loading: isUpdatingAccountMainSelfie,
    error: updateAccountMainSelfieError,
    callback: handleUpdateAccountMainSelfie,
  } = useApiCallback(
    async (selfieId: string) => {
      await SessionClientDashboard.updateResource<Account>(
        {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.account.id,
          type: ApiModel.Account,
        },
        { meta: { 'selfie-id': selfieId } },
      );

      await loadAccount();
      props.showMessage(`Account main selfie updated.`, MessageType.Success);
    },
    [props.showMessage, props.setAccount, props.account, loadAccount],
  );

  useApiErrorHandler({
    error: updateAccountMainSelfieError,
    onError: () =>
      props.showMessage(
        'Failed to update account main selfie. Please try again.',
        MessageType.Failure,
        false,
      ),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const {
    loading: isUpdatingAccountStatus,
    error: updateAccountStatusError,
    callback: handleUpdateAccountStatus,
  } = useApiCallback(
    async (accountStatusId: string) => {
      await SessionClientDashboard.updateResource<Account>(
        {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.account.id,
          type: ApiModel.Account,
        },
        { meta: { 'account-status-id': accountStatusId } },
      );

      await loadAccount();
      props.showMessage(`Account status updated.`, MessageType.Success);
    },
    [props.showMessage, props.setAccount, props.account, loadAccount],
  );

  useApiErrorHandler({
    error: updateAccountStatusError,
    onError: () =>
      props.showMessage('Failed to update account. Please try again.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });
  const { error: updateAccountIdNumbersError, callback: handleUpdateAccountIdNumbers } =
    useApiCallback(
      async (attributes: AccountIdPropertyFormData, editIndex: number) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const legacyUserFields = props.account.attributes.fields as unknown as FieldsMapLegacyUser;
        const newAccount = {
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.account.id,
          type: ApiModel.Account as const,
          attributes: {
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            ...props.account.attributes,
            fields: {
              ...legacyUserFields,
              identification_numbers: {
                type: 'array',
                value: legacyUserFields.identification_numbers.value.map(
                  (idNumber, index: number) => {
                    if (index !== editIndex) {
                      return idNumber;
                    } else {
                      return {
                        type: 'hash',
                        value: {
                          identification_number: {
                            type: 'string',
                            value: attributes['identification-number'],
                          },
                          identification_class: {
                            type: 'string',
                            value: attributes['identification-class'],
                          },
                          issuing_country: {
                            type: 'string',
                            value: attributes['issuing-country'],
                          },
                        },
                      };
                    }
                  },
                ),
              },
            } as FieldsMapLegacyUser,
          },
        };
        await SessionClientDashboard.updateResource(newAccount);
        await loadAccount();
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        await loadAccountReportTemplateFilterOptions(props.account.id);
        props.showMessage('Account updated.', MessageType.Success);
      },
      [
        props.showMessage,
        props.setAccount,
        props.account,
        loadAccount,
        loadAccountReportTemplateFilterOptions,
      ],
    );

  useApiErrorHandler({
    error: updateAccountIdNumbersError,
    onError: () =>
      props.showMessage('Failed to update account. Please try again.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: handleCreateCaseError, callback: handleCreateCase } = useApiCallback(
    async (values: CreateCaseFormData) => {
      const response = await props.createCase({
        environmentId: props.currentEnvironment.id,
        caseTemplateId: values.caseTemplateId,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        accountIds: [props.account.id],
        inquiryIds: values.inquiryIds,
        reportIds: values.reportIds,
      });
      props.showMessage('Case created.', MessageType.Success, false);
      navigate(`/dashboard/cases/${response.data.id}`);
    },
    [props.showMessage, props.account, props.currentEnvironment, props.createCase],
  );

  useApiErrorHandler({
    error: handleCreateCaseError,
    onError: () =>
      props.showMessage('Failed to create case. Please try again.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: handleCreateReportError, callback: handleCreateReport } = useApiCallback(
    async (reportTemplateFilterOption: ReportTemplateFilterOption) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await props.createReport(reportTemplateFilterOption, props.account, props.currentEnvironment);
      props.showMessage(`${reportTemplateFilterOption.name} created`, MessageType.Success);
      await loadAccount();
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      await loadAccountReportTemplateFilterOptions(props.account.id);
    },
    [
      props.showMessage,
      props.account,
      props.currentEnvironment,
      props.createReport,
      loadAccount,
      loadAccountReportTemplateFilterOptions,
    ],
  );

  useApiErrorHandler({
    error: handleCreateReportError,
    onError: () =>
      props.showMessage('Failed to create report. Please try again.', MessageType.Failure, false),
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { callback: handleUpdateTags, error: handleUpdateTagsError } = useApiCallback(
    async ({ tags: tagIds }: FormUpdateTagsData) => {
      await SessionClientDashboard.postResourceAction<Account>(
        {
          type: ApiModel.Account,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.account.id,
        },
        'set-tags',
        { meta: { 'tag-id': tagIds } },
      );

      await loadAccount();
    },
    [props.account, props.accountTags, loadAccount],
  );

  useApiErrorHandler({
    error: handleUpdateTagsError,
    onError: (error) => {
      captureException(error);
      props.showMessage('Failed to add tags to account.', MessageType.Failure);
    },
    onNetworkError: () => props.showMessage('Failed to add tags to account.', MessageType.Failure),
  });

  const { callback: redactAccount } = useApiCallback(
    async (accountId: string) => {
      try {
        const accountResponse = await SessionClientDashboard.deleteResource<Account>(
          ApiEndpoint.Account,
          accountId,
        );

        props.setAccount(accountResponse.data);
        // Optimization to avoid reloading Account since backend should set user as redactor.
        props.setAccountRedactor(props.currentUser);
        props.showMessage('Account marked for redaction.', MessageType.Success);
      } catch (e) {
        captureException(e);
        props.showMessage('Failed to redact account.', MessageType.Failure);
      }
    },
    [
      props.setAccount,
      props.setAccountRedactor,
      props.currentUser,
      props.showMessage,
      captureException,
    ],
  );

  const { error: removeDeviceError, callback: handleRemoveDevice } = useApiCallback(
    async (deviceId: string) => {
      await SessionClientDashboard.postResourceAction<Account>(
        {
          type: ApiModel.Account,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.account.id,
        },
        'remove-device',
        { meta: { 'device-id': deviceId } },
      );

      await loadAccount();
    },
    [props.account, props.devices, loadAccount],
  );

  useApiErrorHandler({
    error: removeDeviceError,
    onError: (error) => {
      captureException(error);
      props.showMessage('Failed to remove device from account.', MessageType.Failure);
    },
    onNetworkError: () =>
      props.showMessage('Failed to remove device from account.', MessageType.Failure),
  });

  const { error: addTagsError, callback: handleAddTags } = useApiCallback(
    async (accountIds: string[], { tags: tagIds }: FormUpdateTagsData) => {
      await SessionClientDashboard.postResourceSingletonAction(
        { type: ApiModel.Account },
        'bulk-add-tags',
        {
          meta: {
            account_ids: accountIds,
            tag_id: tagIds,
          },
        },
      );

      await loadAccount();

      const displayMessage = `Successfully added ${tagIds.length} ${
        tagIds.length > 1 ? 'tags' : 'tag'
      }`;
      props.showMessage(displayMessage, MessageType.Success);
    },
    [props.accountTags, loadAccount],
  );

  useApiErrorHandler({
    error: addTagsError,
    onError: () => {
      props.showMessage('Failed to add tags, please try again.', MessageType.Failure, false);
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  /************************************************
   * START Bulk update account status handler
   ************************************************/
  const { error: bulkUpdateAccountStatusError, callback: handleBulkUpdateAccountStatus } =
    useApiCallback(async (accountIds: string[], accountStatusId: string) => {
      await SessionClientDashboard.postResourceSingletonAction(
        { type: ApiModel.Account },
        'bulk-update-account-status',
        {
          meta: {
            account_ids: accountIds,
            account_status_id: accountStatusId,
          },
        },
      );

      const displayMessage = `Successfully updated status of ${accountIds.length} accounts`;
      props.showMessage(displayMessage, MessageType.Success);
    }, []);

  useApiErrorHandler({
    error: bulkUpdateAccountStatusError,
    onError: () => {
      props.showMessage(
        'Failed to update status for accounts, please try again.',
        MessageType.Failure,
        false,
      );
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
        false,
      ),
  });

  /************************************************
   * END Bulk update account status handler
   ************************************************/

  useEffect(() => {
    if (props.account?.id) {
      loadAccountReportTemplateFilterOptions(props.account.id);
    }
  }, [props.account?.id, loadAccountReportTemplateFilterOptions]);

  let isCreateReportButtonDisabled = !usePermission([
    Permission.ReportWrite,
    Permission.ReportCreate,
  ]);
  if (
    productFeatureDisabled(
      props.organization,
      ProductFeatureName.ReportsCreate,
      props.currentEnvironment.type,
    )
  ) {
    isCreateReportButtonDisabled = true;
  }

  const {
    handlePageLinkClick: handleInquiryPageLinkClick,
    handlePageSizeClick: handleInquiryPageSizeClick,
  } = useTable({
    pageSize: props.inquiryPageSize,
    currentPage: props.inquiryCurrentPage,
    setCurrentPage: props.setInquiryCurrentPage,
    setPageSize: props.setInquiryPageSize,
  });

  const {
    handlePageLinkClick: handleVerificationPageLinkClick,
    handlePageSizeClick: handleVerificationPageSizeClick,
  } = useTable({
    pageSize: props.verificationPageSize,
    currentPage: props.verificationCurrentPage,
    setCurrentPage: props.setVerificationCurrentPage,
    setPageSize: props.setVerificationPageSize,
  });

  const {
    handlePageLinkClick: handleDocumentPageLinkClick,
    handlePageSizeClick: handleDocumentPageSizeClick,
  } = useTable({
    pageSize: props.documentPageSize,
    currentPage: props.documentCurrentPage,
    setCurrentPage: props.setDocumentCurrentPage,
    setPageSize: props.setDocumentPageSize,
  });

  const {
    handlePageLinkClick: handleReportPageLinkClick,
    handlePageSizeClick: handleReportPageSizeClick,
  } = useTable({
    pageSize: props.reportPageSize,
    currentPage: props.reportCurrentPage,
    setCurrentPage: props.setReportCurrentPage,
    setPageSize: props.setReportPageSize,
  });

  // extract default graph query template id if it exists
  const accountTypesFilterOption = props.accountTypesFilterOptions?.find(
    (filterOption) => filterOption.id === accountTypeId,
  );
  const graphQueryTemplateId = accountTypesFilterOption?.['default-graph-query-template-id'];

  return (
    <DashboardAccountsShowView
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      account={props.account}
      accountStatuses={props.accountStatuses}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      accountType={props.accountType}
      accountTypes={props.accountTypes}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      accountTags={props.accountTags}
      accountTypeGridLayouts={props.accountTypeGridLayouts}
      accountVariableDefinitions={props.accountVariableDefinitions}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      accountTypesFilterOption={accountTypesFilterOption}
      accountActions={props.accountActions}
      actionWorkflowVersions={props.actionWorkflowVersion}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      documentPageSize={props.documentPageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      documentPaginationParams={props.documentPaginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportPageSize={props.reportPageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reportPaginationParams={props.reportPaginationParams}
      triggers={props.triggers}
      currentUser={props.currentUser}
      inquiries={props.inquiries}
      inquiryTemplateFilterOptions={props.inquiryTemplateFilterOptions}
      devices={props.devices}
      documents={props.documents}
      cases={props.cases}
      caseTemplateBasicsOptions={props.caseTemplateBasicsOptions}
      isLoadingGraph={
        isLoadingGraph ||
        isLoadingGraphQueryTemplates ||
        props.graphQuery?.attributes?.status === 'submitted'
      }
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      graphQueryTemplateId={graphQueryTemplateId}
      graphQueryTemplates={props.graphQueryTemplates}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      graph={props.graph}
      users={props.users}
      themes={props.themes}
      organization={props.organization}
      currentEnvironment={props.currentEnvironment}
      redactor={props.redactor}
      isCreateReportButtonDisabled={isCreateReportButtonDisabled}
      isLoadingAccount={
        isLoadingAccount ||
        isLoadingAccountTypes ||
        isUpdatingAccountStatus ||
        isUpdatingAccountMainSelfie
      }
      isLoadingAccountActions={isLoadingAccountActions}
      isLoadingDocuments={isLoadingDocuments}
      isLoadingInquiries={isLoadingInquiries}
      isLoadingReports={isLoadingReports}
      isLoadingReportTemplates={isLoadingAccountReportTemplates}
      isLoadingVerifications={isLoadingVerifications}
      featureFlags={props.featureFlags}
      relations={props.relations}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      reports={props.reports}
      selfies={props.selfies}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      verifications={props.verifications}
      creatableReportTemplateFilterOptions={props.creatableReportTemplateFilterOptions}
      handleUpdateAccount={handleUpdateAccount}
      handleCreateIdNumber={handleCreateIdNumber}
      handleUpdateAccountStatus={handleUpdateAccountStatus}
      handleUpdateAccountMainSelfie={handleUpdateAccountMainSelfie}
      handleCreateReport={handleCreateReport}
      handleCreateCase={handleCreateCase}
      handleUpdateTags={handleUpdateTags}
      handleUpdateAccountIdNumbers={handleUpdateAccountIdNumbers}
      handleAddTags={handleAddTags}
      handleRemoveDevice={handleRemoveDevice}
      handleBulkUpdateAccountStatus={handleBulkUpdateAccountStatus}
      handleRunAccountAction={handleRunAccountAction}
      redactAccount={redactAccount}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nullStateMessage={nullStateMessages[errorCode.current]}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiryPaginationParams={props.inquiryPaginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      verificationPaginationParams={props.verificationPaginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inquiryPageSize={props.inquiryPageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      verificationPageSize={props.verificationPageSize}
      lists={props.lists}
      listItems={props.listItems}
      listMatches={props.listMatches}
      handleDocumentPageLinkClick={handleDocumentPageLinkClick}
      handleDocumentPageSizeClick={handleDocumentPageSizeClick}
      handleInquiryPageLinkClick={handleInquiryPageLinkClick}
      handleInquiryPageSizeClick={handleInquiryPageSizeClick}
      handleVerificationPageLinkClick={handleVerificationPageLinkClick}
      handleVerificationPageSizeClick={handleVerificationPageSizeClick}
      handleReportPageLinkClick={handleReportPageLinkClick}
      handleReportPageSizeClick={handleReportPageSizeClick}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    account: state.account.account,
    accountStatuses: state.accountStatuses.accountStatuses,
    accountType: state.account.accountType,
    accountTypes: state.accountTypes.accountTypes,
    accountTypeGridLayouts: state.accountType.gridLayouts,
    accountVariableDefinitions: state.account.variableDefinitions,
    accountTypesFilterOptions: state.accountTypes.filterOptions,
    accountActions: state.accountActions.accountActions,
    actionWorkflowVersion: state.accountActions.actionWorkflowVersions,
    documentPaginationParams: state.account.documentPaginationParams,
    documentPageSize: state.account.documentPageSize,
    documentCurrentPage: state.account.documentCurrentPage,
    triggers: state.accountActions.triggers,
    currentUser: state.currentUser.currentUser,
    users: state.user.users,
    accountTags: state.account.tags,
    redactor: state.account.redactor,
    inquiries: state.account.inquiries,
    inquiryTemplateFilterOptions: getAllTemplateFilterOptions(state),
    graph: state.graph.graph,
    graphData: state.graph.graphData,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    graphQuery: state.graphQuery.graphQuery,
    graphQueryTemplates: state.graphQueryTemplates.graphQueryTemplates,
    organization: state.organization.organization,
    themes: state.organization.themes,
    reports: state.account.reports,
    reportPaginationParams: state.account.reportPaginationParams,
    reportPageSize: state.account.reportPageSize,
    reportCurrentPage: state.account.reportCurrentPage,
    featureFlags: state.featureFlags.featureFlags,
    inquiryPaginationParams: state.account.inquiryPaginationParams,
    verificationPaginationParams: state.account.verificationPaginationParams,
    verificationPageSize: state.account.verificationPageSize,
    verificationCurrentPage: state.account.verificationCurrentPage,
    inquiryCurrentPage: state.account.inquiryCurrentPage,
    inquiryPageSize: state.account.inquiryPageSize,
    devices: state.account.devices,
    // For now only support generic documents in this component. Need to figure out how to display
    // or download government ID documents which can have front or back photos of the ID.
    documents: getAccountGenericDocuments(state),
    cases: state.account.cases,
    caseTemplateBasicsOptions: state.caseTemplates.basicsOptions,
    selfies: state.account.selfies,
    verifications: state.account.verifications,
    relations: state.account.relations,
    // TODO: Get environment from account instead. We need to return the environment data in the
    // account serializer in the backend and also figure out how to toggle the environment button in
    // the sidebar to match the account's environment.
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    creatableReportTemplateFilterOptions: state.reportTemplates.filterOptions,
    lists: state.account.lists,
    listItems: state.account.listItems,
    listMatches: state.account.listMatches,
  };
};

const mapDispatchToProps = {
  clearAccount,
  createCase,
  createReport,
  setAccount,
  loadAccount,
  loadAccountTypes,
  loadAccountTypesFilterOptions,
  loadAccountDocuments,
  loadAccountInquiries,
  loadAccountReports,
  loadAccountVerifications,
  loadCaseTemplateBasicsOptions,
  loadAccountStatuses,
  loadReportTemplatesFilterOptions,
  showMessage,
  loadUserList,
  loadGraphsAndGraph,
  loadGraphQueryTemplates,
  loadAccountActions,
  setAccountRedactor,
  setDocumentCurrentPage,
  setDocumentPageSize,
  setInquiryCurrentPage,
  setInquiryPageSize,
  setVerificationPageSize,
  setVerificationCurrentPage,
  setReportCurrentPage,
  setReportPageSize,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardAccountsShowRoute);
